/**
 * Mobile cart page — swipe left on a line item to reveal delete.
 */
(function () {
  'use strict';

  const SWIPE_OPEN_CLASS = 'is-swipe-open';
  const SWIPE_STRUCTURED_ATTR = 'data-swipe-structured';
  const SWIPE_DELETE_WIDTH = 72;
  const SWIPE_DELETE_GAP = 5;
  const SWIPE_REVEAL_WIDTH = SWIPE_DELETE_WIDTH + SWIPE_DELETE_GAP;
  const SWIPE_THRESHOLD = 36;
  const MOBILE_QUERY = '(max-width: 749px)';
  const CART_UPDATE = 'cart:update';

  /** @type {MediaQueryList} */
  let mediaQuery = window.matchMedia(MOBILE_QUERY);

  /** @type {HTMLTableRowElement | null} */
  let activeRow = null;

  const isEnabled = () => mediaQuery.matches && Boolean(document.querySelector('.cart-page'));

  /**
   * @param {HTMLTableRowElement} row
   * @returns {HTMLElement | null}
   */
  const getSwipePanel = (row) => {
    const panel = row.querySelector('.cart-items__swipe-panel');
    return panel instanceof HTMLElement ? panel : null;
  };

  /**
   * @param {HTMLTableRowElement} row
   * @param {number} offset
   */
  const setRowOffset = (row, offset) => {
    const panel = getSwipePanel(row);
    if (!panel) return;

    panel.style.transform = offset === 0 ? '' : `translateX(${offset}px)`;
  };

  /**
   * @param {HTMLTableRowElement} row
   * @param {boolean} isDragging
   */
  const setRowTransition = (row, isDragging) => {
    const panel = getSwipePanel(row);
    if (!panel) return;

    if (isDragging) {
      panel.style.transition = 'none';
    } else {
      panel.style.removeProperty('transition');
    }
  };

  /**
   * @param {HTMLTableRowElement} row
   */
  const closeRow = (row) => {
    row.classList.remove(SWIPE_OPEN_CLASS);
    setRowOffset(row, 0);
    setRowTransition(row, false);
    if (activeRow === row) activeRow = null;
  };

  /**
   * @param {HTMLTableRowElement | null} except
   */
  const closeAllRows = (except) => {
    for (const row of document.querySelectorAll(`.cart-page .cart-items__table-row.${SWIPE_OPEN_CLASS}`)) {
      if (row !== except) closeRow(row);
    }
  };

  /**
   * @param {HTMLTableRowElement} row
   */
  const openRow = (row) => {
    closeAllRows(row);
    row.classList.add(SWIPE_OPEN_CLASS);
    setRowOffset(row, -SWIPE_REVEAL_WIDTH);
    activeRow = row;
  };

  /**
   * @param {Element} target
   */
  const isSwipeIgnoredTarget = (target) => {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest(
        '.cart-items__remove, quantity-selector-component, .volume-option, .cart-items__tier-discount-details, .cart-items__tier-discount-host summary, a[href]'
      )
    );
  };

  /**
   * @param {HTMLTableCellElement} td
   * @returns {HTMLDivElement}
   */
  const cellToSlot = (td) => {
    const slot = document.createElement('div');

    for (const className of td.classList) {
      slot.classList.add(className);
    }

    slot.classList.add('cart-items__swipe-slot');

    for (const attr of ['headers', 'role', 'ref', 'id']) {
      const value = td.getAttribute(attr);
      if (value) slot.setAttribute(attr, value);
    }

    while (td.firstChild) {
      slot.appendChild(td.firstChild);
    }

    return slot;
  };

  /**
   * @param {HTMLTableRowElement} row
   */
  const structureRowForSwipe = (row) => {
    if (row.getAttribute(SWIPE_STRUCTURED_ATTR) === 'true') return;
    if (!isEnabled()) return;

    const removeCell = row.querySelector(':scope > .cart-items__remove-cell');
    if (!(removeCell instanceof HTMLTableCellElement)) return;

    const contentCells = Array.from(row.querySelectorAll(':scope > td:not(.cart-items__remove-cell)')).filter(
      (cell) => cell instanceof HTMLTableCellElement
    );

    if (contentCells.length === 0) return;

    const removeButton = removeCell.querySelector('.cart-items__remove');

    const swipeCell = document.createElement('td');
    swipeCell.className = 'cart-items__swipe-cell';
    swipeCell.colSpan = contentCells.length + 1;

    const swipe = document.createElement('div');
    swipe.className = 'cart-items__swipe';

    const action = document.createElement('div');
    action.className = 'cart-items__swipe-action';
    action.setAttribute('aria-hidden', 'true');

    if (removeButton instanceof HTMLElement) {
      action.appendChild(removeButton);
    }

    const panel = document.createElement('div');
    panel.className = 'cart-items__swipe-panel';

    for (const cell of contentCells) {
      panel.appendChild(cellToSlot(cell));
      cell.remove();
    }

    removeCell.remove();

    swipe.appendChild(action);
    swipe.appendChild(panel);
    swipeCell.appendChild(swipe);
    row.appendChild(swipeCell);

    row.classList.add('cart-items__table-row--swipe');
    row.setAttribute(SWIPE_STRUCTURED_ATTR, 'true');
  };

  /**
   * @param {HTMLTableRowElement} row
   */
  const bindRow = (row) => {
    structureRowForSwipe(row);

    if (row.dataset.swipeBound === 'true') return;
    row.dataset.swipeBound = 'true';

    let startX = 0;
    let startY = 0;
    let baseOffset = 0;
    let currentOffset = 0;
    let tracking = false;
    let swiping = false;

    row.addEventListener(
      'touchstart',
      (event) => {
        if (!isEnabled()) return;
        if (!getSwipePanel(row)) return;
        if (isSwipeIgnoredTarget(event.target)) return;

        const touch = event.touches[0];
        if (!touch) return;

        startX = touch.clientX;
        startY = touch.clientY;
        baseOffset = row.classList.contains(SWIPE_OPEN_CLASS) ? -SWIPE_REVEAL_WIDTH : 0;
        currentOffset = baseOffset;
        tracking = true;
        swiping = false;
        setRowTransition(row, true);
      },
      { passive: true }
    );

    row.addEventListener(
      'touchmove',
      (event) => {
        if (!tracking || !isEnabled()) return;

        const touch = event.touches[0];
        if (!touch) return;

        const deltaX = touch.clientX - startX;
        const deltaY = touch.clientY - startY;

        if (!swiping) {
          if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 8) {
            tracking = false;
            setRowTransition(row, false);
            return;
          }

          if (Math.abs(deltaX) < 8) return;

          swiping = true;
          closeAllRows(row);
        }

        event.preventDefault();

        currentOffset = Math.max(-SWIPE_REVEAL_WIDTH, Math.min(0, baseOffset + deltaX));
        setRowOffset(row, currentOffset);
      },
      { passive: false }
    );

    const endSwipe = () => {
      if (!tracking || !isEnabled()) return;

      tracking = false;
      setRowTransition(row, false);

      if (!swiping) return;

      swiping = false;

      if (currentOffset <= -SWIPE_THRESHOLD) {
        openRow(row);
        return;
      }

      closeRow(row);
    };

    row.addEventListener('touchend', endSwipe, { passive: true });
    row.addEventListener('touchcancel', endSwipe, { passive: true });

    row.addEventListener('click', (event) => {
      if (!row.classList.contains(SWIPE_OPEN_CLASS)) return;
      if (event.target instanceof Element && event.target.closest('.cart-items__remove')) return;
      closeRow(row);
    });
  };

  const init = () => {
    if (!document.querySelector('.cart-page')) return;

    for (const row of document.querySelectorAll('.cart-page .cart-items__table-row[data-line]')) {
      if (row instanceof HTMLTableRowElement) bindRow(row);
    }
  };

  const resetAllSwipes = () => {
    for (const row of document.querySelectorAll(`.cart-page .cart-items__table-row.${SWIPE_OPEN_CLASS}`)) {
      if (row instanceof HTMLTableRowElement) closeRow(row);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener(CART_UPDATE, () => {
    requestAnimationFrame(init);
  });

  mediaQuery.addEventListener('change', () => {
    resetAllSwipes();
    if (isEnabled()) {
      init();
    }
  });

  document.addEventListener(
    'touchstart',
    (event) => {
      if (!activeRow || !isEnabled()) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('.cart-items__table-row') === activeRow) return;
      closeRow(activeRow);
    },
    { passive: true }
  );
})();
